import { computed, reactive, readonly } from 'vue'
import { EnumsDomStorage, FrontendErrorCode, MetadataKey } from '@/types/enums'
import { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import appStore from '@/stores/app.store'
import apiService from '@/services/api.service'
import accountStore from '@/stores/account.store'
import ApiError from '@/types/ApiError'
import GoogleTagManagerService from '@/services/google-tag-manager.service'
import unitStore from '@/stores/unit.store'

interface accountStoreStates {
  token: string | null
}

const state = reactive({
  token: localStorage.getItem(EnumsDomStorage.AUTH_TOKEN) || null,
} as accountStoreStates)

const checkRouteAuth = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
) => {
  let idToken = null
  let accessToken = ''

  /**
   * Clear PII data from calendly links
   * @see https://careerpartner.atlassian.net/browse/MPAD-1157
   */
  if (to.query && to.query['assigned_to']) {
    return next({ query: {} })
  }

  if (to.path === '/maintenance') {
    return next()
  }

  // Check Auth Redirect and tokens
  if (to.hash && !['#motivation'].includes(to.hash)) {
    // is redirect from Auth0

    const hash = to.hash.replace('#access_token', '?access_token')
    const urlParams = new URLSearchParams(hash)

    accessToken = urlParams.get('access_token') || ''
    idToken = urlParams.get('id_token') || ''

    // load last url path | MPAD-643
    const lastUrl = sessionStorage.getItem(EnumsDomStorage.ORIGIN_URL_PATH)

    // B2B using the auth0 token directly for the authentication with the backend, so no need for /auth (MPAD-1174)
    if (appStore.state.isB2b) {
      state.token = accessToken
      return next(lastUrl ?? to.path)
    }

    // get authentication bearer for B2C from Backend
    await apiService.auth.post(accessToken, idToken).then(
      (result) => {
        state.token = result.data.api_token
        localStorage.setItem(EnumsDomStorage.AUTH_TOKEN, state.token || '')
        return next(lastUrl ?? to.path)
      },
      async (error: ApiError) => {
        console.log('[B2C] /auth check error', error)
        if (!appStore.state.apiError) {
          await appStore.setApiError(
            error.code ?? FrontendErrorCode.AUTH_ERROR,
            error.message,
          )
        }
        return next(lastUrl ?? to.path)
      },
    )
  }

  //## Route call to route without authorization needed (like privacy policy) -> No Token needed
  if (to.meta.allowUnauthorized) {
    appStore.setDisableNavbar(true)
    appStore.toggleInitialLoading()
    return next()
  }

  /** validation of token through apiService **/
  if (!state.token && !appStore.state.apiError) {
    // load last url path | MPAD-643
    sessionStorage.setItem(EnumsDomStorage.ORIGIN_URL_PATH, to.fullPath)
    console.log('[REDIRECT] set lastUrl', to.fullPath)
    sessionStorage.setItem(
      EnumsDomStorage.ORIGIN_URL_QUERIES,
      JSON.stringify({ time: new Date(), queries: to.query }),
    )
    await appStore.redirectToLogin()
    return false
  }

  // Don't redirect to onboarding if user is viewing document preview
  if (to.name === 'DocumentsPreview') {
    return next()
  }

  if (
    !appStore.state.initialLoading &&
    !onboardingDone.value &&
    to.meta.onboarding &&
    to.name !== to.meta.onboarding
  ) {
    return next({ name: 'ds.home' })
  }

  // disable navbar if in route meta
  appStore.setDisableNavbar(!!to.meta.noNav)

  return next()
}

const onboardingDone = computed(() => {
  const onboardingIsDone = accountStore.getMetadataValue(
    MetadataKey.ONBOARDING_DONE,
  )
  const interviewIsDone = unitStore.state?.unit?.participateAdvisorInterviewOn

  return onboardingIsDone || interviewIsDone
})

const logoutUser = () => {
  GoogleTagManagerService.trackEvent('LogoutClick', 'logout')
  // TODO: Logout in backend
  localStorage.clear()
  const logoutUrl = appStore.state.isB2b
    ? `${import.meta.env.VITE_AUTH0_LOGOUT_URL}${appStore.state.baseUrl}`
    : `${import.meta.env.VITE_AUTH0_B2C_LOGOUT_URL}${appStore.state.baseUrl}`
  console.log('logoutUser with redirect', logoutUrl)
  window.location.replace(logoutUrl)
}

// Export complete store at once
export default {
  state: readonly(state),
  checkRouteAuth,
  logoutUser,
  onboardingDone,
}
